import { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Pagination } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "@/utils/appToast";
import ContentContainer from "@/components/containers/contentContainer";
import { api } from "@/utils/api";
import { getBackendCallErrorMessage } from "@/utils/backendErrorMessage";
import JobSkeleton from "@/utils/jobskeleton";

type InboxStatus =
  | "all"
  | "awaiting"
  | "submitted"
  | "accepted"
  | "rejected"
  | "converted";

const statusBadgeClass = (status: string) => {
  if (status === "awaiting")
    return "bg-amber-50 text-amber-800 border-amber-200";
  if (status === "submitted")
    return "bg-[#e7eeff] text-[#4285F4] border-[#d8e3fb]";
  if (status === "accepted")
    return "bg-green-50 text-green-700 border-green-200";
  if (status === "rejected") return "bg-red-50 text-red-700 border-red-200";
  if (status === "converted")
    return "bg-[#f0f2f8] text-[#424654] border-[#d8e3fb]";
  return "bg-[#f9f9ff] text-[#424654] border-[#d8e3fb]";
};

const getClientName = (row: any) => {
  const client = row?.client;
  if (!client || typeof client !== "object") return "-";
  const name = [client.first_name, client.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || "-";
};

const getServiceName = (row: any, lang?: string) => {
  const service = row?.service;
  if (!service) return "-";
  if (typeof service === "string") return service;
  return (
    service?.mlname?.[lang || "en"] ||
    service?.mlname?.en ||
    service?.name ||
    "-"
  );
};

const unwrapInbox = (res: any) => {
  const body = res?.success !== undefined ? res.data : res;
  const list = Array.isArray(body?.data)
    ? body.data
    : Array.isArray(body?.data?.data)
      ? body.data.data
      : [];
  return {
    rows: list,
    totalPages: Number(body?.totalPages ?? body?.data?.totalPages) || 1,
    total: Number(body?.total ?? body?.data?.total) || 0,
  };
};

export default function ProviderRequestQuotesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<any[]>([]);
  const [fetching, setFetching] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<InboxStatus>("all");
  const pageSize = 10;

  const fetchInbox = useCallback(
    async (pageNum = page, status: InboxStatus = statusFilter) => {
      setFetching(true);
      try {
        const res = await api.getProviderRequestQuoteInbox({
          page: pageNum,
          limit: pageSize,
          status,
        });
        if (res?.success === false) {
          toast.error(
            getBackendCallErrorMessage(res) ||
              res?.message ||
              "Failed to load request quotes"
          );
          setRows([]);
          setTotalPages(1);
          return;
        }
        const { rows: list, totalPages: pages } = unwrapInbox(res);
        setRows(list);
        setTotalPages(pages);
      } catch (err: any) {
        toast.error(err?.message || "Failed to load request quotes");
        setRows([]);
        setTotalPages(1);
      } finally {
        setFetching(false);
      }
    },
    [page, statusFilter]
  );

  useEffect(() => {
    void fetchInbox(page, statusFilter);
  }, [page, statusFilter, fetchInbox]);

  const filters: { key: InboxStatus; label: string }[] = [
    { key: "all", label: t("all") || "All" },
    { key: "awaiting", label: t("proposalStatus_awaiting") || "Awaiting" },
    { key: "submitted", label: t("proposalStatus_submitted") || "Submitted" },
    { key: "accepted", label: t("proposalStatus_accepted") || "Accepted" },
    { key: "rejected", label: t("proposalStatus_rejected") || "Rejected" },
  ];

  const openDetail = (row: any) => {
    const id = row?.requestQuoteJobId || row?.jobUid;
    if (!id) return;
    navigate(`/provider-request-quotes/${id}`, { state: { inboxRow: row } });
  };

  return (
    <ContentContainer className="!px-0 m-0 bg-[#f9f9ff] min-h-screen">
      <div className="px-4 sm:px-6 pt-6 pb-2">
        <h1 className="text-xl sm:text-2xl font-bold text-[#111c2d]">
          {t("providerRequestQuotes") || "Request Quotes"}
        </h1>
        <p className="text-sm text-[#424654] mt-1">
          {t("providerRequestQuotesNote") ||
            "Quotes clients sent to you. Open one to create or update your proposal."}
        </p>
      </div>

      <div className="px-4 sm:px-6 py-6 flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => {
                setStatusFilter(f.key);
                setPage(1);
              }}
              className={`h-9 px-4 rounded-lg text-sm font-semibold border transition-colors ${
                statusFilter === f.key
                  ? "bg-[#4285F4] text-white border-[#4285F4]"
                  : "bg-white text-[#4285F4] border-[#d8e3fb] hover:bg-[#e7eeff]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {!fetching &&
          rows.map((row) => {
            const status = String(row?.proposalStatus || "awaiting").toLowerCase();
            const cta =
              status === "awaiting"
                ? t("create_proposal") || "Create proposal"
                : t("edit_proposal") || "Edit proposal";
            const requestedDate = row.sentAt || row.createdAt;

            return (
              <div
                key={row.requestQuoteJobId || row.jobUid}
                className="bg-white rounded-xl border border-[#d8e3fb] shadow-sm p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6"
              >
                <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4 xl:gap-5">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#424654]">
                      {t("jobId")}
                    </p>
                    <p
                      className="text-sm font-semibold text-[#111c2d] truncate mt-0.5"
                      title={row.jobUid || row.requestQuoteJobId}
                    >
                      {row.jobUid || row.requestQuoteJobId || "-"}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#424654]">
                      {t("service_name")}
                    </p>
                    <p
                      className="text-sm text-[#111c2d] truncate mt-0.5"
                      title={getServiceName(row, i18n.language)}
                    >
                      {getServiceName(row, i18n.language)}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#424654]">
                      {t("client") || "Client"}
                    </p>
                    <p
                      className="text-sm font-semibold text-[#111c2d] truncate mt-0.5"
                      title={getClientName(row)}
                    >
                      {getClientName(row)}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#424654]">
                      {t("status")}
                    </p>
                    <span
                      className={`inline-flex w-fit max-w-full items-center rounded-full border px-3 py-1 text-xs font-semibold capitalize mt-1 ${statusBadgeClass(
                        status
                      )}`}
                    >
                      {t(`proposalStatus_${status}`) || status}
                    </span>
                  </div>
                  <div className="min-w-0 col-span-2 sm:col-span-1">
                    <p className="text-xs font-medium text-[#424654]">
                      {t("requestedDate") || "Requested date"}
                    </p>
                    <p className="text-sm text-[#111c2d] mt-0.5 whitespace-nowrap">
                      {requestedDate
                        ? moment(requestedDate).format("DD MMM YYYY")
                        : "-"}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => openDetail(row)}
                  className="h-10 shrink-0 w-full lg:w-auto px-5 rounded-lg bg-[#4285F4] text-white text-sm font-semibold shadow-sm hover:bg-[#4285F4] transition-colors whitespace-nowrap"
                >
                  {cta}
                </button>
              </div>
            );
          })}

        {!fetching && rows.length === 0 && (
          <div className="bg-white rounded-xl border border-[#d8e3fb] px-6 py-12 text-center">
            <p className="text-sm text-[#424654]">
              {t("noProviderRequestQuotesYet") ||
                "No request quotes in your inbox yet."}
            </p>
          </div>
        )}

        {fetching && (
          <div className="flex flex-col gap-4">
            {[...Array(5)].map((_, i) => (
              <JobSkeleton key={i} />
            ))}
          </div>
        )}

        <div className="flex justify-center pt-2">
          <Pagination
            count={totalPages}
            color="primary"
            variant="outlined"
            page={page}
            onChange={(_e, p) => setPage(p)}
            showFirstButton
            showLastButton
            siblingCount={0}
          />
        </div>
      </div>
    </ContentContainer>
  );
}
